﻿import React, { useState, useEffect, useCallback, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { BellIcon, XMarkIcon, CheckIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline'
import { NotificationRecipientItem } from '../../services/notifications'
import { Button } from '../ui/button'
import { Tooltip } from '../ui/tooltip'
import { getSignedAvatarUrl } from '../../services/avatar'
import { useNotifications } from '../../hooks/useNotifications'
import NotificationDetailModal from './NotificationDetailModal'
import { renderTextWithLinks } from './notificationTextLinks'
import { fetchProfilesDisplay, formatUserNameWithDepartment } from '../../utils/userDisplayName'

const BROWSER_PROMPT_DISMISSED_KEY = 'qg-browser-notifications-prompt-dismissed'

interface Props {
  items: NotificationRecipientItem[]
  onMarkAsRead: (id: string) => void
  onDelete: (id: string) => void
  onClearAll: () => void
  onClose: () => void
  /** Elemento de referência (área da foto/menu do usuário) para posicionar o painel. */
  anchorRef: React.RefObject<HTMLElement | null>
}

// Função para formatar timestamp relativo
const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  
  if (diffInSeconds < 60) return 'Agora'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m atrás`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h atrás`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d atrás`
  return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })
}

// Função para gerar inicial do nome
const getInitials = (text: string): string => {
  if (!text) return 'N'
  const words = text.trim().split(/\s+/)
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase()
  }
  return text.substring(0, 2).toUpperCase()
}

// Função para gerar cor do avatar
const getAvatarColor = (text: string): string => {
  const colors = [
    'bg-purple-500',
    'bg-primary-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-red-500',
    'bg-teal-500',
  ]
  let hash = 0
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}

const NotificationMenu: React.FC<Props> = ({ items, onMarkAsRead, onDelete, onClearAll, onClose, anchorRef }) => {
  const navigate = useNavigate()
  const { browserPermission, requestBrowserPermission } = useNotifications()
  const unreadCount = items.filter(it => !it.read_at).length
  const [selectedNotification, setSelectedNotification] = useState<NotificationRecipientItem | null>(null)
  const [senderInfo, setSenderInfo] = useState<{ name: string; avatarUrl: string | null; departmentName?: string | null } | null>(null)
  const [loadingAvatar, setLoadingAvatar] = useState<boolean>(false)
  const [sendersMap, setSendersMap] = useState<Record<string, { name: string; avatarUrl: string | null; departmentName?: string | null }>>({})
  const [loadingSenders, setLoadingSenders] = useState<boolean>(false)
  const [promptDismissed, setPromptDismissed] = useState(() => {
    try {
      return localStorage.getItem(BROWSER_PROMPT_DISMISSED_KEY) === '1'
    } catch {
      return false
    }
  })
  const [requestingPermission, setRequestingPermission] = useState(false)

  const showPermissionPrompt = browserPermission === 'default' && !promptDismissed
  const showPermissionDenied = browserPermission === 'denied'

  const handleDismissPrompt = () => {
    setPromptDismissed(true)
    try {
      localStorage.setItem(BROWSER_PROMPT_DISMISSED_KEY, '1')
    } catch {
      // ignore
    }
  }

  const handleEnableBrowserNotifications = async () => {
    setRequestingPermission(true)
    try {
      const result = await requestBrowserPermission()
      if (result === 'granted') {
        const { showBrowserNotification } = await import('../../utils/browserNotifications')
        showBrowserNotification({
          title: 'PASI QG',
          body: 'Notificações do navegador ativadas.',
          tag: 'qg-browser-notifications-enabled',
        })
      }
    } finally {
      setRequestingPermission(false)
    }
  }

  // Buscar informações dos remetentes de todas as notificações
  useEffect(() => {
    const fetchAllSenders = async () => {
      if (items.length === 0) {
        setSendersMap({})
        setLoadingSenders(false)
        return
      }

      // Coletar todos os IDs únicos de remetentes
      const senderIds = Array.from(
        new Set(
          items
            .map(it => it.notification?.created_by)
            .filter(Boolean) as string[]
        )
      )

      if (senderIds.length === 0) {
        setSendersMap({})
        setLoadingSenders(false)
        return
      }

      setLoadingSenders(true)
      try {
        const profiles = await fetchProfilesDisplay(senderIds)

        // Criar mapa de remetentes com avatares
        const senders: Record<string, { name: string; avatarUrl: string | null; departmentName?: string | null }> = {}
        
        for (const profile of profiles) {
          let avatarUrl: string | null = null
          if (profile.avatar_url) {
            avatarUrl = await getSignedAvatarUrl(profile.avatar_url)
          }
          
          senders[profile.id] = {
            name: profile.name || 'Usuário',
            avatarUrl,
            departmentName: profile.departmentName,
          }
        }

        // Preencher com "Usuário" para IDs não encontrados
        senderIds.forEach(id => {
          if (!senders[id]) {
            senders[id] = { name: 'Usuário', avatarUrl: null }
          }
        })

        setSendersMap(senders)
      } catch (err) {
        console.error('Erro ao buscar informações dos remetentes:', err)
        setSendersMap({})
      } finally {
        setLoadingSenders(false)
      }
    }

    fetchAllSenders()
  }, [items])

  // Resetar estados quando uma nova notificação é selecionada
  useEffect(() => {
    if (selectedNotification) {
      setSenderInfo(null)
      setLoadingAvatar(true)
    }
  }, [selectedNotification?.id])

  // Buscar informações do usuário que criou a notificação
  useEffect(() => {
    const fetchSenderInfo = async () => {
      if (!selectedNotification?.notification?.created_by) {
        setSenderInfo(null)
        setLoadingAvatar(false)
        return
      }

      setLoadingAvatar(true)
      try {
        const profiles = await fetchProfilesDisplay([selectedNotification.notification.created_by])
        const profile = profiles[0]

        if (!profile) {
          setSenderInfo({ name: 'Usuário', avatarUrl: null, departmentName: null })
          setLoadingAvatar(false)
          return
        }

        // Buscar URL assinada do avatar se existir
        let avatarUrl: string | null = null
        if (profile.avatar_url) {
          avatarUrl = await getSignedAvatarUrl(profile.avatar_url)
        }

        setSenderInfo({
          name: profile.name || 'Usuário',
          avatarUrl,
          departmentName: profile.departmentName,
        })
      } catch (err) {
        console.error('Erro ao buscar informações do remetente:', err)
        setSenderInfo({ name: 'Usuário', avatarUrl: null, departmentName: null })
      } finally {
        setLoadingAvatar(false)
      }
    }

    fetchSenderInfo()
  }, [selectedNotification])

  const handleInternalNavigate = useCallback((path: string) => {
    onClose()
    setSelectedNotification(null)
    navigate(path)
  }, [navigate, onClose])

  const linkOptions = { onInternalNavigate: handleInternalNavigate }

  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({
    position: 'fixed',
    zIndex: 9990,
    visibility: 'hidden',
  })

  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchor = anchorRef.current
      if (!anchor) return
      const rect = anchor.getBoundingClientRect()
      setPanelStyle({
        position: 'fixed',
        left: Math.max(8, rect.left + 16),
        bottom: Math.max(8, window.innerHeight - rect.top + 8),
        zIndex: 9990,
        width: '24rem',
        maxWidth: 'calc(100vw - 1rem)',
        visibility: 'visible',
      })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('resize', updatePosition)
    }
  }, [anchorRef])

  const panel = (
    <div
      style={panelStyle}
      className="max-h-[calc(100vh-5rem)] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notificações</h2>
            {unreadCount > 0 && (
              <span className="px-2.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium">
                {unreadCount} Nova{unreadCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-2"
            aria-label="Fechar notificações"
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {showPermissionPrompt && (
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-primary-50 dark:bg-primary-900/20 flex-shrink-0">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-700 dark:text-gray-300">
                Ative as notificações do navegador para receber avisos com a aba em segundo plano.
              </p>
              <Button
                size="sm"
                className="mt-2"
                onClick={handleEnableBrowserNotifications}
                disabled={requestingPermission}
              >
                Ativar
              </Button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismissPrompt}
              className="p-1 flex-shrink-0"
              aria-label="Dispensar aviso de notificações do navegador"
            >
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {showPermissionDenied && (
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 flex-shrink-0">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Notificações do navegador bloqueadas nas configurações do navegador.
          </p>
        </div>
      )}

      {/* Lista de notificações */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {items.length === 0 ? (
          <div className="p-8 text-center">
            <BellIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Sem notificações</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {items.map((it) => {
              const title = it.notification?.title || 'Sem título'
              const body = it.notification?.body || ''
              const isUnread = !it.read_at
              const createdAt = it.created_at || it.notification?.created_at || ''
              const senderId = it.notification?.created_by
              const sender = senderId ? sendersMap[senderId] : null
              const senderName = sender?.name || 'Usuário'
              const senderLabel = formatUserNameWithDepartment(sender?.name, sender?.departmentName)
              const senderAvatarUrl = sender?.avatarUrl || null
              
              return (
                <div
                  key={it.id}
                  className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors relative group"
                >
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full ${loadingSenders || !sender ? 'bg-gray-300 dark:bg-gray-600' : senderAvatarUrl ? '' : getAvatarColor(senderName)} flex items-center justify-center text-white font-semibold text-sm overflow-hidden`}>
                      {loadingSenders || !sender ? (
                        <div className="w-full h-full flex items-center justify-center bg-gray-300 dark:bg-gray-600 animate-pulse"></div>
                      ) : senderAvatarUrl ? (
                        <img 
                          src={senderAvatarUrl} 
                          alt={senderName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        getInitials(senderName)
                      )}
                    </div>

                    {/* Conteúdo */}
                    <div className="flex-1 min-w-0 pr-20">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1 break-words">
                        {renderTextWithLinks(title, linkOptions)}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-2 break-words overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {renderTextWithLinks(body, linkOptions)}
                      </div>
                      <button
                        onClick={() => setSelectedNotification(it)}
                        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 underline transition-colors cursor-pointer"
                        type="button"
                      >
                        <EyeIcon className="h-3.5 w-3.5" />
                        Ver mensagem completa
                      </button>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                        {createdAt ? formatTimeAgo(createdAt) : ''}
                        {senderName && (
                          <>
                            {' · '}
                            Enviado por {senderLabel}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Indicador de não lida e botões de ação */}
                    <div className="absolute top-4 right-4 flex items-start gap-2">
                      {/* Indicador de não lida - sempre visível */}
                      {isUnread && (
                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-purple-500 mt-2.5 relative z-20"></div>
                      )}

                      {/* Botões de ação (aparecem no hover) */}
                      <div className="opacity-0 group-hover:opacity-100 flex flex-col gap-1.5 transition-all relative z-10">
                        {/* Botão de marcar como visualizada */}
                        {isUnread && (
                          <Tooltip content="Marcar como visualizada" position="left">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation()
                                onMarkAsRead(it.id)
                              }}
                              className="p-1.5 h-auto text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
                              aria-label="Marcar como visualizada"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                        )}
                        
                        {/* Botão de excluir */}
                        <Tooltip content="Excluir notificação" position="left">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              e.stopPropagation()
                              if (window.confirm('Excluir esta notificação?')) {
                                onDelete(it.id)
                              }
                            }}
                            className="p-1.5 h-auto text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                            aria-label="Excluir notificação"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {items.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
          <Button
            variant="solid"
            onClick={() => {
              if (window.confirm('Tem certeza que deseja limpar todas as notificações?')) {
                onClearAll()
              }
            }}
            className="w-full"
          >
            Limpar minhas notificações
          </Button>
        </div>
      )}

      {/* Modal de Notificação Completa - mesmo componente usado no Dashboard */}
      {selectedNotification && (
        <NotificationDetailModal
          notification={selectedNotification}
          senderInfo={senderInfo}
          loadingAvatar={loadingAvatar}
          onClose={() => setSelectedNotification(null)}
          onMarkAsRead={async (id) => {
            await onMarkAsRead(id)
            setSelectedNotification(null)
          }}
          onDelete={async (id) => {
            await onDelete(id)
            setSelectedNotification(null)
          }}
          onInternalNavigate={handleInternalNavigate}
        />
      )}
    </div>
  )

  return createPortal(panel, document.body)
}

export default NotificationMenu


